import React from 'react';

export const franklinDRoosevelt = {
  presidentNumber: 32,
  president: 'Franklin D. Roosevelt',
  subheader: 'The New Deal',
  years: '1933-1945',
  predecessor: 'Herbert Hoover',
  successor: 'Harry S. Truman',
  letter: {
    header: 'Letter to Harry S. Truman',
    content: `My Dear Harry,
As I write these final words, I am sustained by confidence that our New Deal has preserved American democracy through its greatest trial since the Civil War. When I assumed this office twelve years ago, our nation faced economic collapse and the real possibility that democratic government might prove unable to address industrial society's challenges. We have demonstrated that democracy can adapt and endure.

The programs we created—Social Security, unemployment insurance, bank deposit guarantees, and federal regulation of finance—have established the principle that government must serve as the ultimate guarantor of economic security for ordinary Americans. These achievements represent a new birth of freedom, freeing our people from the fear of destitution that haunted previous generations.

Yet our greatest trial has been the war against fascism that has consumed the latter years of my presidency. We have led the world's democracies to victory over totalitarian enemies that threatened to destroy everything we hold sacred. America has emerged from this conflict as the leader of the free world, with responsibilities that will test your wisdom as they have tested mine.

I confess regret that our alliance with Soviet Russia, necessary for victory over Germany, has created new tensions that may prove as dangerous as fascism itself. Stalin's ambitions in Eastern Europe suggest that the peace we have won may prove temporary if democracy lacks the strength to resist communist expansion.

The atomic weapon that our scientists have developed gives America unprecedented power but also unprecedented responsibility. This terrible force must be controlled by international agreement, or it will make future wars unthinkably destructive.

Trust the American people, for they have never failed democracy when properly led. The presidency is a place of moral leadership that can inspire our nation to achievements beyond ordinary imagination.

Your predecessor in democratic faith, Franklin D. Roosevelt`
  },
  biographical: [
    'Born January 30, 1882, in Hyde Park, New York',
    'Served partial term, March 4, 1933 – April 12, 1945',
    'Died in office, succeeded by Harry S. Truman',
    'Previously served as Governor of New York (1929-1933)',
    'Only president elected to four terms'
  ],
  presidencyFacts: [
    'Led America through Great Depression and World War II',
    'Created New Deal programs to combat economic crisis',
    'Established Social Security and minimum wage',
    'Led Allied Powers to victory in World War II',
    'Died just before war\'s end'
  ],
  milestones: [
    'First 100 Days of New Deal (1933)',
    'Social Security Act passed (1935)',
    'Pearl Harbor attack (1941)',
    'D-Day invasion (1944)',
    'Died on April 12, 1945, in Warm Springs'
  ],
  personalLife: [
    'Married Eleanor Roosevelt in 1905',
    'Had six children, five survived to adulthood',
    'Contracted polio in 1921, used wheelchair',
    'Known for his fireside chats and optimism',
    'Died on April 12, 1945, in Warm Springs, Georgia'
  ],
  impactAndLegacy: [
    'Transformed American government and society',
    'Created modern welfare state and regulatory system',
    'Led America to victory in World War II',
    'Established America as world superpower',
    'Ranked among top three presidents by historians'
  ],
  postPresidency: [
    'Died in office on April 12, 1945',
    'Buried at Hyde Park, New York',
    'Became symbol of presidential leadership',
    'FDR Memorial dedicated in Washington (1997)',
    'Remains most consequential 20th century president'
  ],
  historicalAnalysis: `Franklin D. Roosevelt's presidency fundamentally transformed American government and society through New Deal domestic reforms and leadership during World War II, establishing the modern welfare state and America's role as global superpower.

[SECTION] The New Deal and Economic Recovery

Roosevelt's response to the Great Depression created unprecedented federal programs that addressed immediate relief needs while establishing permanent changes in government's role in economic and social welfare.

The First Hundred Days (1933) demonstrated the potential for rapid governmental action, with Congress passing major legislation including the Emergency Banking Act, Agricultural Adjustment Act, and National Industrial Recovery Act.

The Second New Deal (1935-1936) created more permanent and significant programs, including Social Security, the Wagner Act protecting labor rights, and the Works Progress Administration providing employment for millions of unemployed Americans.

These programs established the principle that federal government bore responsibility for individual economic security, fundamentally altering the relationship between citizens and their government.

[SECTION] Financial and Banking Reform

Roosevelt's banking reforms restored public confidence in the financial system through federal deposit insurance, stronger regulation, and separation of commercial and investment banking.

The Glass-Steagall Act (1933) separated commercial and investment banking while creating the Federal Deposit Insurance Corporation, eliminating bank runs that had destroyed savings throughout the depression.

The Securities Exchange Commission (1934) regulated stock markets and required corporate disclosure, preventing many of the fraudulent practices that had contributed to the 1929 crash.

These financial reforms created stability that lasted for decades and demonstrated how appropriate regulation could preserve capitalism while protecting consumers and investors.

[SECTION] Labor Relations and Social Policy

The Wagner Act (1935) established federal protection for workers' rights to organize and bargain collectively, fundamentally altering the balance of power between labor and management.

Social Security created the first national old-age pension system, providing basic retirement security for American workers while establishing precedents for further expansion of social welfare programs.

The Fair Labor Standards Act (1938) established federal minimum wage and maximum hour standards, extending federal authority into areas previously left to state and local governments.

These labor and social policies created a more equitable distribution of economic power and income, contributing to the growth of the American middle class in subsequent decades.

[SECTION] World War II Leadership

Roosevelt's leadership during World War II demonstrated the potential for presidential power during national emergencies while establishing America as the leader of the democratic world.

The Lend-Lease program (1941) provided military aid to Britain and other allies before American entry into the war, effectively ending neutrality while avoiding formal belligerency.

Pearl Harbor unified American public opinion behind the war effort, enabling Roosevelt to mobilize national resources on an unprecedented scale for global conflict.

The president's wartime diplomacy, including relationships with Churchill and Stalin, shaped the post-war international order while creating alliances that would define the Cold War era.

[SECTION] Expansion of Presidential Power

Roosevelt's presidency established the modern imperial presidency through unprecedented expansion of executive authority justified by national emergencies.

The creation of numerous federal agencies and programs expanded presidential control over domestic policy while establishing administrative capabilities that future presidents would inherit.

Roosevelt's use of executive orders, radio addresses, and direct appeals to public opinion created new models of presidential leadership that bypassed traditional congressional prerogatives.

However, the Supreme Court's initial resistance to New Deal programs demonstrated constitutional limitations on presidential power that required careful political management and occasional compromise.

[SECTION] Civil Rights and Social Justice

Roosevelt's record on civil rights was mixed, reflecting both progressive impulses and political constraints imposed by Southern Democratic support.

Eleanor Roosevelt's advocacy for civil rights and the inclusion of African Americans in some New Deal programs represented progress, though many programs remained segregated or excluded minorities.

[SECTION] Economic Philosophy and Keynesian Influence

Roosevelt's economic policies, while not strictly Keynesian, established principles of federal economic intervention that influenced policy for decades.

Deficit spending for unemployment relief and public works demonstrated that government fiscal policy could stimulate economic activity, though Roosevelt never fully embraced deficit spending as economic theory.

The creation of countercyclical programs like unemployment insurance and agricultural support established automatic stabilizers that would moderate future economic downturns.

[SECTION] Foreign Policy and International Leadership

Roosevelt's foreign policy evolved from cautious neutrality to active leadership of the democratic alliance against fascism, fundamentally altering America's international role.

The Atlantic Charter (1941) established democratic principles for the post-war world while creating the foundation for the United Nations and other international organizations.

Roosevelt's vision of the United States as one of the "Four Policemen" (with Britain, Soviet Union, and China) reflected his understanding that America could not return to isolationism after the war.

[SECTION] Death and Legacy

Roosevelt's death on April 12, 1945, just before Allied victory in Europe, transformed him into a martyr for democracy and the New Deal while creating questions about post-war leadership.

His unprecedented four terms established him as a transformational president whose impact on American government and society exceeded any president since Lincoln.

[SECTION] Historical Assessment

Historical evaluation of Roosevelt has been consistently positive, with most surveys ranking him among the greatest presidents alongside Washington and Lincoln.

Scholars praise his restoration of public confidence, creation of the welfare state, and leadership during World War II while acknowledging controversies over executive power and civil rights.

Conservative critics argue that Roosevelt's expansion of federal power undermined constitutional principles and individual liberty, while liberal critics fault his failure to address racial injustice more aggressively.

Modern assessments recognize Roosevelt as the creator of modern American liberalism and the architect of America's emergence as a global superpower.`,
  sources: [
    '[LINK:https://archive.org/details/franklindrooseve00leuc|Leuchtenburg, William E. Franklin D. Roosevelt and the New Deal, 1932-1940. Harper & Row, 1963.]',
    '[LINK:https://archive.org/details/rooseveltlionfox00burn|Burns, James MacGregor. Roosevelt: The Lion and the Fox and Roosevelt: The Soldier of Freedom. Harcourt Brace Jovanovich, 1956, 1970.]',
    '[LINK:https://global.oup.com/academic/product/freedom-from-fear-9780195038347|Kennedy, David M. Freedom from Fear: The American People in Depression and War, 1929-1945. Oxford University Press, 1999.]',
    '[LINK:https://millercenter.org/president/fdroosevelt/impact-and-legacy|Miller Center, University of Virginia. "Franklin D. Roosevelt: Impact and Legacy." Accessed 2025.]',
    '[LINK:https://www.history.com/topics/us-presidents/franklin-d-roosevelt|History.com. "Franklin D. Roosevelt: Facts, Presidency & Death." Updated February 2025.]',
    '[LINK:https://www.whitehousehistory.org/bios/franklin-d-roosevelt|White House Historical Association. "Franklin D. Roosevelt." Accessed 2025.]',
    '[LINK:https://en.wikipedia.org/wiki/Presidency_of_Franklin_D._Roosevelt|Wikipedia. "Presidency of Franklin D. Roosevelt." Updated 2025.]'
  ]
};

export default franklinDRoosevelt;